import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Check, X, RefreshCw, Loader2, List, ArrowRight, Copy, Link2, ShieldOff } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext.tsx';

// Funções do Google
import {
  getGoogleCalendarIntegration,
  updateGoogleCalendarIntegration,
  deleteGoogleCalendarIntegration,
} from '../services/api.ts';

// Funções Clinicorp
import {
  getClinicorpIntegration,
  saveClinicorpIntegration,
  deleteClinicorpIntegration,
  getSelectableClinicorpDetails,
  saveSelectedClinicorpDetails,
  getClinicorpWebhook,
  getClinicorpSyncPreflight,
  provisionClinicorpWebhook,
  rotateClinicorpWebhook,
  revokeClinicorpWebhook,
  ClinicorpIntegrationPayload,
  ClinicorpBusinessItem,
  ClinicorpUserItem,
  SelectedDetailsPayload,
  ClinicorpWebhookState,
  ClinicorpSyncPreflightState,
} from '../services/clinicorpApi.ts';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal.tsx';
import ClinicorpPatientPolicy from '../components/ClinicorpPatientPolicy.tsx';

// Importando as imagens
import { IMAGES } from '../utils/images.ts';

// Interface combinada para o estado local (mantida do original)
interface ClinicorpStateData extends ClinicorpIntegrationPayload {
  business_id?: number | null;
  dentist_person_id?: number | null;
}



// --- Componente Principal ---
const AgendaIntegration = () => {
  const { isDark } = useTheme();
  // Estados Gerais
  const [selectedIntegration, setSelectedIntegration] = useState<'google' | 'clinicorp' | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasIntegration, setHasIntegration] = useState(false);
  const [clinicId, setClinicId] = useState<number | null>(null);

  // Estados Google
  const [googleCalendarId, setGoogleCalendarId] = useState('');

  // Estados Clinicorp (mantidos do original)
  const [clinicorpData, setClinicorpData] = useState<ClinicorpStateData>({
    username: '',
    password: '',
    code_link: '',
    subscriber_id: '',
    business_id: null,
    dentist_person_id: null,
  });

  // Estados para o Fluxo de Seleção Clinicorp
  const [showSelectionStep, setShowSelectionStep] = useState(false);
  const [selectableBusinesses, setSelectableBusinesses] = useState<ClinicorpBusinessItem[]>([]);
  const [selectableUsers, setSelectableUsers] = useState<ClinicorpUserItem[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
  const [selectedDentistId, setSelectedDentistId] = useState<string>('');
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [clinicorpWebhook, setClinicorpWebhook] = useState<ClinicorpWebhookState | null>(null);
  const [clinicorpWebhookUrl, setClinicorpWebhookUrl] = useState<string | null>(null);
  const [clinicorpWebhookError, setClinicorpWebhookError] = useState<string | null>(null);
  const [clinicorpPreflight, setClinicorpPreflight] = useState<ClinicorpSyncPreflightState | null>(null);
  const [clinicorpPatientPolicy, setClinicorpPatientPolicy] = useState<{
    enabled: boolean;
    canManage: boolean;
  } | null>(null);
  const [isWebhookBusy, setIsWebhookBusy] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmRotateOpen, setConfirmRotateOpen] = useState(false);
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);

  // --- Funções ---
  // Busca as integrações existentes ao montar e define o estado inicial
  const fetchIntegrations = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    setIsEditing(false);
    setShowSelectionStep(false);
    setSelectedBusinessId('');
    setSelectedDentistId('');
    setSelectableBusinesses([]);
    setSelectableUsers([]);
    setClinicorpWebhook(null);
    setClinicorpWebhookUrl(null);
    setClinicorpWebhookError(null);
    setClinicorpPreflight(null);
    setClinicorpPatientPolicy(null);

    let foundIntegration = false;

    try {
      // Tenta Google
      const googleData = await getGoogleCalendarIntegration();
      if (googleData && googleData.google_calendar_id) {
        setSelectedIntegration('google');
        setGoogleCalendarId(googleData.google_calendar_id);
        setHasIntegration(true);
        foundIntegration = true;
      }
    } catch (err: any) {
      console.warn('Erro ou nenhuma integração Google encontrada:', err.message);
    }

    // Tenta Clinicorp (APENAS se Google não foi encontrada)
    if (!foundIntegration) {
      try {
        const clinicorpResult = await getClinicorpIntegration();
        if (clinicorpResult && (clinicorpResult.username || clinicorpResult.subscriber_id)) {
          setSelectedIntegration('clinicorp');
          setClinicorpData({
            username: clinicorpResult.username || '',
            password: '',
            code_link: clinicorpResult.code_link || '',
            subscriber_id: clinicorpResult.subscriber_id || '',
            business_id: clinicorpResult.business_id ?? null,
            dentist_person_id: clinicorpResult.dentist_person_id ?? null
          });
          setClinicorpPatientPolicy({
            enabled: Boolean(clinicorpResult.ignore_same_name_on_patient_create),
            canManage: Boolean(clinicorpResult.can_manage_patient_create_policy),
          });
          setHasIntegration(true);
          foundIntegration = true;
          if (clinicorpResult.business_id && clinicorpResult.dentist_person_id) {
            try {
              let webhook = await getClinicorpWebhook(clinicId);
              if (webhook.status === 'not_configured') {
                webhook = await provisionClinicorpWebhook(clinicId);
              }
              setClinicorpWebhook(webhook);
              setClinicorpWebhookUrl(webhook.webhook_url || null);
              setClinicorpPreflight(await getClinicorpSyncPreflight(clinicId));
            } catch (webhookError: any) {
              setClinicorpWebhookError(
                webhookError.message || 'Não foi possível carregar o webhook Clinicorp.'
              );
              console.warn('Não foi possível carregar o status do webhook Clinicorp:', webhookError.message);
            }
          }
        }
      } catch (err: any) {
        console.warn('Erro ou nenhuma integração Clinicorp encontrada:', err.message);
      }
    }

    // Se não achou nenhuma integração
    if (!foundIntegration) {
      setSelectedIntegration(null);
      setHasIntegration(false);
      setGoogleCalendarId('');
      setClinicorpData({ username: '', password: '', code_link: '', subscriber_id: '', business_id: null, dentist_person_id: null });
      setClinicorpWebhook(null);
      setClinicorpWebhookUrl(null);
      setClinicorpWebhookError(null);
      setClinicorpPreflight(null);
      setClinicorpPatientPolicy(null);
    }

    setLoading(false);
  }, [clinicId]);

  // Carrega dados ao montar o componente
  useEffect(() => {
    const storedClinicId = localStorage.getItem('clinic_id');
    if (storedClinicId) {
      const id = parseInt(storedClinicId, 10);
      if (!isNaN(id)) {
        setClinicId(id);
        fetchIntegrations();
      } else {
        toast.error("ID da clínica inválido no localStorage.");
        setLoading(false);
      }
    } else {
      toast.error("ID da clínica não encontrado no localStorage.");
      setLoading(false);
    }
  }, [fetchIntegrations]);

  // Handler para SALVAR CREDENCIAIS (Etapa 1)
  const handleSaveCredentials = async () => {
    if (!clinicId) {
      toast.error("ID da clínica não está definido.");
      return;
    }

    const rawCodeLink = clinicorpData.code_link?.trim();
    const validCodeLink = !rawCodeLink || /^[^\u0000-\u0020\u007F]{1,255}$/.test(rawCodeLink);
    if (selectedIntegration === 'clinicorp' && !validCodeLink) {
      toast.error("O Code Link deve ser um código sem espaços ou caracteres de controle.");
      return;
    }

    setIsSaving(true);

    try {
      let shouldFetchDetails = false;

      if (selectedIntegration === 'google') {
        await updateGoogleCalendarIntegration(googleCalendarId);
        toast.success('Integração Google Calendar salva com sucesso!');
        setIsEditing(false);
        fetchIntegrations();
      } else if (selectedIntegration === 'clinicorp') {
        const payload: ClinicorpIntegrationPayload = {
          username: clinicorpData.username,
          subscriber_id: clinicorpData.subscriber_id
        };
        if (rawCodeLink) {
          payload.code_link = rawCodeLink;
        }
        
        if (clinicorpData.password) {
          payload.password = clinicorpData.password;
        }

        await saveClinicorpIntegration(payload);
        toast.success('Credenciais Clinicorp salvas!');

        if (payload.subscriber_id) {
          shouldFetchDetails = true;
        } else {
          toast.warning("Subscriber ID não preenchido. Não é possível buscar detalhes da clínica/profissional.");
          setIsEditing(false);
          fetchIntegrations();
        }
      }

      if (shouldFetchDetails) {
        await handleFetchSelectableDetails();
      }

    } catch (saveError: any) {
      toast.error(`Erro ao salvar credenciais: ${saveError.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handler para BUSCAR DETALHES SELECIONÁVEIS (Etapa 1.5 Clinicorp)
  const handleFetchSelectableDetails = async () => {
    if (!clinicId) return;
    setIsFetchingDetails(true);
    setShowSelectionStep(true);
    setSelectableBusinesses([]);
    setSelectableUsers([]);
    setSelectedBusinessId('');
    setSelectedDentistId('');

    try {
      const response = await getSelectableClinicorpDetails(clinicId);
      setSelectableBusinesses(response.businesses || []);
      setSelectableUsers(response.users || []);
      
      if(response.message) {
        toast.warning(response.message);
      }
      if (response.businesses?.length === 0) {
        toast.warning("Nenhuma clínica (business) encontrada para este subscriber ID no Clinicorp.");
      }
      if (response.users?.length === 0) {
        toast.warning("Nenhum usuário (profissional) encontrado para este subscriber ID no Clinicorp.");
      }
    } catch (fetchError: any) {
      toast.error(`Erro ao buscar detalhes do Clinicorp: ${fetchError.message}`);
      setShowSelectionStep(false);
      setIsEditing(false);
      fetchIntegrations();
    } finally {
      setIsFetchingDetails(false);
    }
  };

  // Handler para CONFIRMAR E SALVAR SELEÇÃO (Etapa 2 Clinicorp)
  const handleConfirmSelection = async () => {
    if (!clinicId || !selectedBusinessId || !selectedDentistId) {
      toast.error("Por favor, selecione uma clínica e um profissional.");
      return;
    }
    
    setIsSavingSelection(true);
    
    try {
      const payload: SelectedDetailsPayload = {
        business_id: parseInt(selectedBusinessId, 10),
        dentist_person_id: parseInt(selectedDentistId, 10)
      };
      
      const response = await saveSelectedClinicorpDetails(clinicId, payload);
      toast.success(response.message || "Detalhes selecionados salvos com sucesso!");

      setShowSelectionStep(false);
      setIsEditing(false);
      await fetchIntegrations();
      if (response.webhook) {
        setClinicorpWebhookError(null);
        setClinicorpWebhook(response.webhook);
        setClinicorpWebhookUrl(response.webhook.webhook_url || null);
        if (response.webhook.webhook_url) {
          toast.info('Link do webhook criado. Copie-o para configurar no Clinicorp.');
        }
      }

    } catch(error: any) {
      toast.error(`Erro ao salvar seleção: ${error.message}`);
    } finally {
      setIsSavingSelection(false);
    }
  };

  // Handler para deletar integração
  const handleDelete = () => {
    if (!clinicId || !selectedIntegration) return;
    setConfirmDeleteOpen(true);
  };

  const performDelete = async () => {
    if (!clinicId || !selectedIntegration) return;

    setIsSaving(true);
    try {
      if (selectedIntegration === 'google') {
        await deleteGoogleCalendarIntegration();
        setGoogleCalendarId('');
      } else if (selectedIntegration === 'clinicorp') {
        await deleteClinicorpIntegration();
        setClinicorpData({ username: '', password: '', code_link: '', subscriber_id: '', business_id: null, dentist_person_id: null });
        setClinicorpWebhook(null);
        setClinicorpWebhookUrl(null);
        setClinicorpWebhookError(null);
        setClinicorpPreflight(null);
        setClinicorpPatientPolicy(null);
      }
      
      toast.success(`Integração ${selectedIntegration === 'google' ? 'Google Agenda' : 'Clinicorp'} removida com sucesso!`);
      setSelectedIntegration(null);
      setHasIntegration(false);
      setIsEditing(false);
    } catch (error: any) {
      toast.error(`Erro ao deletar integração: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const refreshClinicorpWebhook = async () => {
    if (!clinicId) return;
    setIsWebhookBusy(true);
    try {
      const webhook = await getClinicorpWebhook(clinicId);
      const preflight = await getClinicorpSyncPreflight(clinicId);
      setClinicorpWebhookError(null);
      setClinicorpWebhook(webhook);
      setClinicorpPreflight(preflight);
      toast.info(
        webhook.status === 'captured'
          ? 'O Clinicorp já enviou um payload para este link.'
          : 'Ainda não recebemos um payload do Clinicorp.'
      );
    } catch (error: any) {
      setClinicorpWebhookError(error.message || 'Não foi possível atualizar o status do webhook.');
      toast.error(error.message || 'Não foi possível atualizar o status do webhook.');
    } finally {
      setIsWebhookBusy(false);
    }
  };

  const copyClinicorpWebhook = async () => {
    if (!clinicorpWebhookUrl) return;
    try {
      await navigator.clipboard.writeText(clinicorpWebhookUrl);
      toast.success('Link do webhook copiado.');
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const regenerateClinicorpWebhook = async () => {
    if (!clinicId) return;
    setIsWebhookBusy(true);
    try {
      const webhook = await rotateClinicorpWebhook(clinicId);
      const preflight = await getClinicorpSyncPreflight(clinicId);
      setClinicorpWebhookError(null);
      setClinicorpWebhook(webhook);
      setClinicorpWebhookUrl(webhook.webhook_url || null);
      setClinicorpPreflight(preflight);
      toast.success('Novo link do webhook gerado. O link anterior não funciona mais.');
    } catch (error: any) {
      setClinicorpWebhookError(error.message || 'Não foi possível gerar um novo link.');
      toast.error(error.message || 'Não foi possível gerar um novo link.');
    } finally {
      setIsWebhookBusy(false);
    }
  };

  const disableClinicorpWebhook = async () => {
    if (!clinicId) return;
    setIsWebhookBusy(true);
    try {
      const webhook = await revokeClinicorpWebhook(clinicId);
      const preflight = await getClinicorpSyncPreflight(clinicId);
      setClinicorpWebhookError(null);
      setClinicorpWebhook(webhook);
      setClinicorpWebhookUrl(null);
      setClinicorpPreflight(preflight);
      toast.success('Webhook Clinicorp revogado.');
    } catch (error: any) {
      setClinicorpWebhookError(error.message || 'Não foi possível revogar o webhook.');
      toast.error(error.message || 'Não foi possível revogar o webhook.');
    } finally {
      setIsWebhookBusy(false);
    }
  };

  // Handler para inputs Clinicorp
  const handleClinicorpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setClinicorpData(prev => ({ ...prev, [name]: value }));
  };

  // --- Componentes de UI Redesenhados ---
  
  // Tela de carregamento
  const LoadingScreen = () => (
    <div className={`min-h-screen p-4 sm:p-8 ${
      isDark ? 'bg-gradient-to-b from-slate-950 to-slate-900' : 'bg-gradient-to-b from-slate-50 to-white'
    }`}>
      <div className={`max-w-3xl mx-auto rounded-2xl shadow-xl p-8 ${
        isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-slate-200'
      }`}>
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin"></div>
          <span className={`font-medium ${
            isDark ? 'text-gray-300' : 'text-slate-600'
          }`}>Carregando integrações...</span>
        </div>
      </div>
    </div>
  );

  // Seletor de Integração (tela inicial)
  const IntegrationSelector = () => (
    <div className="space-y-6">
      <div className={`pb-6 border-b ${
        isDark ? 'border-gray-700' : 'border-slate-200'
      }`}>
        <h1 className={`text-3xl font-bold ${
          isDark ? 'text-white' : 'text-slate-900'
        }`}>Integração</h1>
        <p className={`mt-2 ${
          isDark ? 'text-gray-300' : 'text-slate-600'
        }`}>
          Conecte seu sistema de agenda para sincronização automática de compromissos
        </p>
      </div>
      
      <div className="mt-8 space-y-4">
        <h2 className={`text-xl font-semibold ${
          isDark ? 'text-gray-200' : 'text-slate-800'
        }`}>Selecione um provedor de agenda:</h2>
        
        <div className="grid grid-cols-1 gap-4 mt-4">
          <div 
            onClick={() => { setSelectedIntegration('google'); setIsEditing(true); }}
            className={`group relative overflow-hidden rounded-2xl shadow-xl border transition-all cursor-pointer ${
              isDark 
                ? 'bg-gray-800 border-gray-700 hover:shadow-2xl hover:border-sminy-primary/50'
                : 'bg-white border-slate-200 hover:shadow-2xl hover:border-sminy-primary/50'
            }`}
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-sminy-primary opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="p-6 flex items-center">
              <div className={`flex-shrink-0 w-16 h-16 flex items-center justify-center rounded-xl ${
                isDark ? 'bg-gray-700' : 'bg-slate-50'
              }`}>
                <img src={IMAGES.GOOGLE_LOGO} alt="Google Calendar" className="w-10 h-10 object-contain" />
              </div>
              <div className="ml-5 flex-grow">
                <h3 className={`text-lg font-semibold ${
                  isDark ? 'text-gray-200' : 'text-slate-900'
                }`}>Google Agenda</h3>
                <p className={`mt-1 ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>Conecte-se com seu calendário Google para sincronizar eventos</p>
              </div>
              <ArrowRight className={`w-6 h-6 transition-colors ${
                isDark 
                  ? 'text-gray-500 group-hover:text-sminy-primary'
                  : 'text-slate-400 group-hover:text-sminy-primary'
              }`} />
            </div>
          </div>
          
          <div 
            onClick={() => { setSelectedIntegration('clinicorp'); setIsEditing(true); }}
            className={`group relative overflow-hidden rounded-2xl shadow-xl border transition-all cursor-pointer ${
              isDark 
                ? 'bg-gray-800 border-gray-700 hover:shadow-2xl hover:border-sminy-primary/50'
                : 'bg-white border-slate-200 hover:shadow-2xl hover:border-sminy-primary/50'
            }`}
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-sminy-primary opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="p-6 flex items-center">
              <div className={`flex-shrink-0 w-16 h-16 flex items-center justify-center rounded-xl ${
                isDark ? 'bg-gray-700' : 'bg-slate-50'
              }`}>
                <img src={IMAGES.CLINICORP_LOGO} alt="Clinicorp" className="w-10 h-10 object-contain" />
              </div>
              <div className="ml-5 flex-grow">
                <h3 className={`text-lg font-semibold ${
                  isDark ? 'text-gray-200' : 'text-slate-900'
                }`}>Clinicorp</h3>
                <p className={`mt-1 ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>Conecte-se com sua conta da plataforma Clinicorp</p>
              </div>
              <ArrowRight className={`w-6 h-6 transition-colors ${
                isDark 
                  ? 'text-gray-500 group-hover:text-sminy-primary'
                  : 'text-slate-400 group-hover:text-sminy-primary'
              }`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Formulário Google
  const GoogleSetupForm = () => (
    <div className={`rounded-2xl shadow-xl overflow-hidden border ${
      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'
    }`}>
      <div className="h-2 bg-sminy-primary"></div>
      <div className="p-6">
        <div className="flex items-center mb-6">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
            isDark ? 'bg-gray-700' : 'bg-slate-50'
          }`}>
            <img src={IMAGES.GOOGLE_LOGO} alt="Google Calendar" className="w-10 h-10 object-contain" />
          </div>
          <h2 className={`text-xl font-semibold ${
            isDark ? 'text-white' : 'text-slate-800'
          }`}>Configurar Google Agenda</h2>
        </div>
        
        <div className={`border rounded-2xl p-4 mb-6 ${
          isDark 
            ? 'bg-sminy-primary/10 border-sminy-primary/20'
            : 'bg-sminy-primary/5 border-sminy-primary/20'
        }`}>
          <p className={`text-sm ${
            isDark ? 'text-sminy-primary/90' : 'text-sminy-primary'
          }`}>
            Insira o ID do calendário Google que deseja integrar com o sistema.
          </p>
        </div>
        
        <div className="space-y-6">
          <div>
            <label htmlFor="googleCalendarId" className={`block text-sm font-medium mb-1 ${
              isDark ? 'text-gray-300' : 'text-slate-700'
            }`}>
              ID do Calendário <span className="text-red-500">*</span>
            </label>
            <input 
              id="googleCalendarId" 
              type="text" 
              value={googleCalendarId} 
              onChange={(e) => setGoogleCalendarId(e.target.value)} 
              placeholder="ex: exemplo@gmail.com"
              disabled={isSaving}
              className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
                isDark 
                  ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
                  : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
              }`}
            />
            <p className={`mt-1 text-xs ${
              isDark ? 'text-gray-400' : 'text-slate-500'
            }`}>
              Encontre este ID nas configurações do seu Google Agenda
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Formulário Clinicorp
  const ClinicorpSetupForm = () => (
    <div className={`rounded-2xl shadow-xl overflow-hidden border ${
      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'
    }`}>
      <div className="h-2 bg-sminy-primary"></div>
      <div className="p-6">
        <div className="flex items-center mb-6">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
            isDark ? 'bg-gray-700' : 'bg-slate-50'
          }`}>
            <img src={IMAGES.CLINICORP_LOGO} alt="Clinicorp" className="w-10 h-10 object-contain" />
          </div>
          <h2 className={`text-xl font-semibold ${
            isDark ? 'text-white' : 'text-slate-800'
          }`}>Configurar Clinicorp</h2>
        </div>
        
        <div className={`border rounded-2xl p-4 mb-6 ${
          isDark 
            ? 'bg-sminy-primary/10 border-sminy-primary/20'
            : 'bg-sminy-primary/5 border-sminy-primary/20'
        }`}>
          <p className={`text-sm ${
            isDark ? 'text-sminy-primary/90' : 'text-sminy-primary'
          }`}>
            Preencha as credenciais fornecidas pelo Clinicorp. Após salvar, você poderá selecionar a clínica e o profissional.
          </p>
        </div>
        
        <div className="space-y-5">
          <div>
            <label htmlFor="subscriber_id" className={`block text-sm font-medium mb-1 ${
              isDark ? 'text-gray-300' : 'text-slate-700'
            }`}>
              Subscriber ID <span className="text-red-500">*</span>
            </label>
            <input 
              id="subscriber_id" 
              name="subscriber_id" 
              type="text" 
              value={clinicorpData.subscriber_id} 
              onChange={handleClinicorpChange} 
              placeholder="Insira o Subscriber ID"
              disabled={isSaving}
              className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
                isDark 
                  ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
                  : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
              }`}
            />
          </div>
          
          <div>
            <label htmlFor="password" className={`block text-sm font-medium mb-1 ${
              isDark ? 'text-gray-300' : 'text-slate-700'
            }`}>
              Token da API <span className="text-red-500">*</span>
            </label>
            <input 
              id="password" 
              name="password" 
              type="password" 
              value={clinicorpData.password} 
              onChange={handleClinicorpChange} 
              placeholder="Insira o token de API"
              disabled={isSaving}
              className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
                isDark 
                  ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
                  : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
              }`}
            />
          </div>
          
          <div>
            <label htmlFor="username" className={`block text-sm font-medium mb-1 ${
              isDark ? 'text-gray-300' : 'text-slate-700'
            }`}>
              Username
            </label>
            <input 
              id="username" 
              name="username" 
              type="text" 
              value={clinicorpData.username} 
              onChange={handleClinicorpChange} 
              placeholder="Se diferente do Subscriber ID"
              disabled={isSaving}
              className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
                isDark 
                  ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
                  : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
              }`}
            />
          </div>
          
          <div>
            <label htmlFor="code_link" className={`block text-sm font-medium mb-1 ${
              isDark ? 'text-gray-300' : 'text-slate-700'
            }`}>
              Code Link
            </label>
            <input 
              id="code_link" 
              name="code_link" 
              type="text" 
              value={clinicorpData.code_link} 
              onChange={handleClinicorpChange} 
              placeholder="Código fornecido pelo Clinicorp"
              disabled={isSaving}
              className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
                isDark 
                  ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
                  : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
              }`}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Tela de seleção de detalhes Clinicorp
  const ClinicorpSelectionScreen = () => (
    <div className="space-y-6">
      <div className={`pb-6 border-b flex items-center ${
        isDark ? 'border-gray-700' : 'border-slate-200'
      }`}>
        <button 
          onClick={() => { setShowSelectionStep(false); setIsEditing(true); }}
          className={`mr-4 p-2 rounded-xl transition-colors ${
            isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
          disabled={isFetchingDetails || isSavingSelection}
        >
          <X className="w-5 h-5" />
        </button>
        <h1 className={`text-2xl font-bold ${
          isDark ? 'text-white' : 'text-slate-900'
        }`}>Selecionar Detalhes Clinicorp</h1>
      </div>
      
      <div className={`rounded-2xl shadow-xl overflow-hidden border ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'
      }`}>
        <div className="h-2 bg-sminy-primary"></div>
        <div className="p-6">
          <div className="flex items-center mb-6">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
              isDark ? 'bg-gray-700' : 'bg-slate-50'
            }`}>
              <img src={IMAGES.CLINICORP_LOGO} alt="Clinicorp" className="w-10 h-10 object-contain" />
            </div>
            <h2 className={`text-xl font-semibold ${
              isDark ? 'text-white' : 'text-slate-800'
            }`}>Selecionar Clínica e Profissional</h2>
          </div>
          
          {isFetchingDetails ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-sminy-primary animate-spin mb-4" />
              <p className={`${
                isDark ? 'text-gray-300' : 'text-slate-600'
              }`}>Buscando dados no Clinicorp...</p>
            </div>
          ) : (
            <>
              <div className={`border rounded-2xl p-4 mb-6 ${
                isDark 
                  ? 'bg-sminy-primary/10 border-sminy-primary/20'
                  : 'bg-sminy-primary/5 border-sminy-primary/20'
              }`}>
                <p className={`text-sm ${
                  isDark ? 'text-sminy-primary/90' : 'text-sminy-primary'
                }`}>
                  Selecione a clínica (Business) e o profissional (Dentista) que deseja integrar com o sistema.
                </p>
              </div>
              
              <div className="space-y-5">
                <div>
                  <label htmlFor="businessSelect" className={`block text-sm font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-slate-700'
                  }`}>
                    Clínica <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="businessSelect"
                    value={selectedBusinessId}
                    onChange={(e) => setSelectedBusinessId(e.target.value)}
                    disabled={isSavingSelection || selectableBusinesses.length === 0}
                    className={`w-full rounded-xl border px-3 py-2 text-sm focus:ring-2 focus:ring-sminy-primary transition-all ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
                        : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
                    }`}
                  >
                    <option value="" disabled>
                      {selectableBusinesses.length === 0 ? '-- Nenhuma clínica encontrada --' : '-- Selecione uma clínica --'}
                    </option>
                    {selectableBusinesses.map(business => (
                      <option key={business.id} value={business.id}>
                        {business.Name} (ID: {business.id})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="dentistSelect" className={`block text-sm font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-slate-700'
                  }`}>
                    Profissional <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="dentistSelect"
                    value={selectedDentistId}
                    onChange={(e) => setSelectedDentistId(e.target.value)}
                    disabled={isSavingSelection || selectableUsers.length === 0}
                    className={`w-full rounded-xl border px-3 py-2 text-sm focus:ring-2 focus:ring-sminy-primary transition-all ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
                        : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
                    }`}
                  >
                    <option value="" disabled>
                      {selectableUsers.length === 0 ? '-- Nenhum profissional encontrado --' : '-- Selecione um profissional --'}
                    </option>
                    {selectableUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} (ID: {user.id})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      <div className="flex justify-end space-x-3 mt-6">
        <button
          onClick={() => { setShowSelectionStep(false); setIsEditing(true); }}
          disabled={isSavingSelection}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center space-x-2"
        >
          <X className="w-4 h-4" />
          <span>Voltar</span>
        </button>
        
        <button
          onClick={handleConfirmSelection}
          disabled={isSavingSelection || !selectedBusinessId || !selectedDentistId}
          className="px-5 py-2 rounded-xl bg-sminy-primary text-white hover:bg-sminy-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isSavingSelection ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Salvando...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span>Confirmar Seleção</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Tela de visualização Google
  const GoogleConnectedView = () => (
    <div className={`rounded-2xl shadow-xl overflow-hidden border ${
      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'
    }`}>
      <div className="h-2 bg-sminy-primary"></div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
              isDark ? 'bg-gray-700' : 'bg-slate-50'
            }`}>
              <img src={IMAGES.GOOGLE_LOGO} alt="Google Calendar" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <h2 className={`text-xl font-semibold ${
                isDark ? 'text-white' : 'text-slate-800'
              }`}>Google Agenda</h2>
              <span className={`inline-flex items-center px-2.5 py-0.5 mt-1 rounded-full text-xs font-medium ${
                isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
              }`}>
                Conectado
              </span>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button 
              onClick={() => setIsEditing(true)}
              className="p-2 rounded-xl text-sminy-primary hover:bg-sminy-primary/10 transition-colors"
              title="Editar configurações"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button 
              onClick={handleDelete}
              className={`p-2 rounded-xl transition-colors ${
                isDark 
                  ? 'text-red-400 hover:bg-red-900/20' 
                  : 'text-red-600 hover:bg-red-50'
              }`}
              title="Remover integração"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className={`border rounded-2xl p-5 ${
          isDark 
            ? 'bg-gray-700 border-gray-600' 
            : 'bg-slate-50 border-slate-200'
        }`}>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0">
              <span className={`text-sm font-medium sm:w-32 flex-shrink-0 ${
                isDark ? 'text-gray-400' : 'text-slate-500'
              }`}>Calendar ID:</span>
              <span className={`text-sm font-mono truncate ${
                isDark ? 'text-gray-200' : 'text-slate-900'
              }`} title={googleCalendarId}>{googleCalendarId}</span>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0">
              <span className={`text-sm font-medium sm:w-32 flex-shrink-0 ${
                isDark ? 'text-gray-400' : 'text-slate-500'
              }`}>Status:</span>
              <span className={`text-sm font-medium flex items-center ${
                isDark ? 'text-green-300' : 'text-green-600'
              }`}>
                <Check className="w-4 h-4 mr-1" /> Ativo e sincronizando
              </span>
            </div>
          </div>
        </div>
        
        <div className={`mt-4 border rounded-2xl p-4 ${
          isDark 
            ? 'bg-sminy-primary/10 border-sminy-primary/20'
            : 'bg-sminy-primary/5 border-sminy-primary/20'
        }`}>
          <p className={`text-sm flex items-center ${
            isDark ? 'text-sminy-primary/90' : 'text-sminy-primary'
          }`}>
            <Check className="w-4 h-4 mr-2" />
            Eventos do Google Agenda serão sincronizados automaticamente
          </p>
        </div>
      </div>
    </div>
  );

  // Tela de visualização Clinicorp
  const ClinicorpConnectedView = () => (
    <div className={`rounded-2xl shadow-xl overflow-hidden border ${
      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'
    }`}>
      <div className="h-2 bg-sminy-primary"></div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
              isDark ? 'bg-gray-700' : 'bg-slate-50'
            }`}>
              <img src={IMAGES.CLINICORP_LOGO} alt="Clinicorp" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <h2 className={`text-xl font-semibold ${
                isDark ? 'text-white' : 'text-slate-800'
              }`}>Clinicorp</h2>
              <span className={`inline-flex items-center px-2.5 py-0.5 mt-1 rounded-full text-xs font-medium ${
                isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
              }`}>
                Conectado
              </span>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button 
              onClick={() => setIsEditing(true)}
              className="p-2 rounded-xl text-sminy-primary hover:bg-sminy-primary/10 transition-colors"
              title="Editar configurações"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button 
              onClick={handleDelete}
              className={`p-2 rounded-xl transition-colors ${
                isDark 
                  ? 'text-red-400 hover:bg-red-900/20' 
                  : 'text-red-600 hover:bg-red-50'
              }`}
              title="Remover integração"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className={`border rounded-2xl p-4 ${
            isDark 
              ? 'bg-gray-700 border-gray-600' 
              : 'bg-slate-50 border-slate-200'
          }`}>
            <h3 className={`text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-slate-700'
            }`}>Credenciais</h3>
            <div className="space-y-2">
              <div>
                <span className={`text-xs ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>Subscriber ID:</span>
                <p className={`${
                  isDark ? 'text-gray-200' : 'text-slate-900'
                }`}>{clinicorpData.subscriber_id || '-'}</p>
              </div>
              <div>
                <span className={`text-xs ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>Username:</span>
                <p className={`${
                  isDark ? 'text-gray-200' : 'text-slate-900'
                }`}>{clinicorpData.username || '-'}</p>
              </div>
              {clinicorpData.code_link && (
                <div>
                  <span className={`text-xs ${
                    isDark ? 'text-gray-400' : 'text-slate-500'
                  }`}>Code Link:</span>
                  <p className={`mt-1 break-all font-mono text-sm ${
                    isDark ? 'text-gray-200' : 'text-slate-900'
                  }`}>{clinicorpData.code_link}</p>
                </div>
              )}
            </div>
          </div>
          
          <div className={`border rounded-2xl p-4 ${
            isDark 
              ? 'bg-gray-700 border-gray-600' 
              : 'bg-slate-50 border-slate-200'
          }`}>
            <h3 className={`text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-slate-700'
            }`}>Detalhes da Integração</h3>
            <div className="space-y-2">
              <div>
                <span className={`text-xs ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>Clínica (Business ID):</span>
                <p className={`${
                  isDark ? 'text-gray-200' : 'text-slate-900'
                }`}>
                  {clinicorpData.business_id ?? <span className={`italic ${
                    isDark ? 'text-orange-400' : 'text-orange-600'
                  }`}>Não definido</span>}
                </p>
              </div>
              <div>
                <span className={`text-xs ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>Profissional (Dentist Person ID):</span>
                <p className={`${
                  isDark ? 'text-gray-200' : 'text-slate-900'
                }`}>
                  {clinicorpData.dentist_person_id ?? <span className={`italic ${
                    isDark ? 'text-orange-400' : 'text-orange-600'
                  }`}>Não definido</span>}
                </p>
              </div>
            </div>
          </div>
        </div>

        {clinicId && clinicorpPatientPolicy && (
          <div className="mb-4">
            <ClinicorpPatientPolicy
              key={clinicId}
              clinicId={clinicId}
              initialValue={clinicorpPatientPolicy.enabled}
              canManage={clinicorpPatientPolicy.canManage}
              isDark={isDark}
            />
          </div>
        )}

        {clinicorpPreflight && (
          <div className={`mb-4 rounded-2xl border p-4 ${
            clinicorpPreflight.status === 'ready'
              ? (isDark ? 'border-green-800 bg-green-950/30' : 'border-green-200 bg-green-50')
              : clinicorpPreflight.status === 'degraded'
                ? (isDark ? 'border-amber-800 bg-amber-950/30' : 'border-amber-200 bg-amber-50')
                : (isDark ? 'border-red-900 bg-red-950/30' : 'border-red-200 bg-red-50')
          }`}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-slate-800'}`}>
                  Diagnóstico da sincronização
                </h3>
                <p className={`mt-1 text-xs ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                  {clinicorpPreflight.status === 'ready'
                    ? 'CRM e Clinicorp estão prontos para sincronizar a agenda de avaliação.'
                    : clinicorpPreflight.status === 'degraded'
                      ? 'A configuração está ativa, mas existem eventos ou operações que precisam de reconciliação.'
                      : 'A sincronização está bloqueada por configuração incompleta.'}
                </p>
              </div>
              <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-medium ${
                clinicorpPreflight.status === 'ready'
                  ? (isDark ? 'bg-green-900/60 text-green-200' : 'bg-green-100 text-green-800')
                  : clinicorpPreflight.status === 'degraded'
                    ? (isDark ? 'bg-amber-900/60 text-amber-200' : 'bg-amber-100 text-amber-800')
                    : (isDark ? 'bg-red-900/60 text-red-200' : 'bg-red-100 text-red-800')
              }`}>
                {clinicorpPreflight.status === 'ready' ? 'Pronto' : clinicorpPreflight.status === 'degraded' ? 'Revisar' : 'Bloqueado'}
              </span>
            </div>
            <div className={`mt-3 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
              <span>Fuso: {clinicorpPreflight.timezone || 'não configurado'}</span>
              <span>Confirmação: {clinicorpPreflight.confirmation_status === 'configured' ? 'configurada' : 'não configurada'}</span>
              <span>Eventos em revisão: {clinicorpPreflight.quarantined_events + clinicorpPreflight.failed_events}</span>
              <span>Operações pendentes: {clinicorpPreflight.unresolved_operations}</span>
            </div>
          </div>
        )}
        
        {clinicorpData.business_id && clinicorpData.dentist_person_id && (
          <div className={`mb-4 rounded-2xl border p-4 sm:p-5 ${
            isDark ? 'border-gray-600 bg-gray-700' : 'border-slate-200 bg-slate-50'
          }`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-sminy-primary" />
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-slate-800'}`}>
                    Webhook Clinicorp
                  </h3>
                </div>
                <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  Link exclusivo desta clínica para receber ajustes feitos diretamente no Clinicorp.
                </p>
              </div>
              <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-medium ${
                clinicorpWebhookError
                  ? (isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700')
                  : clinicorpWebhook?.status === 'captured'
                  ? (isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800')
                  : clinicorpWebhook?.status === 'revoked'
                    ? (isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700')
                    : (isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800')
              }`}>
                {clinicorpWebhookError
                  ? 'Indisponível'
                  : clinicorpWebhook?.status === 'captured'
                  ? 'Payload recebido'
                  : clinicorpWebhook?.status === 'revoked'
                    ? 'Revogado'
                    : 'Aguardando payload'}
              </span>
            </div>

            <div className={`mt-4 rounded-xl border p-3 ${
              isDark ? 'border-gray-600 bg-gray-800/60' : 'border-slate-200 bg-white'
            }`}>
              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Link para configurar no Clinicorp
              </span>
              <p className={`mt-1 break-all font-mono text-xs ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>
                {clinicorpWebhookUrl || clinicorpWebhook?.token_preview || (clinicorpWebhookError ? 'Link indisponível' : 'Gerando link...')}
              </p>
              {clinicorpWebhookError && (
                <p role="alert" className={`mt-2 text-xs ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                  {clinicorpWebhookError}
                </p>
              )}
              {!clinicorpWebhookUrl && clinicorpWebhook?.token_preview && clinicorpWebhook.status !== 'revoked' && (
                <p className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  Por segurança, o link completo aparece somente quando é criado ou regenerado.
                </p>
              )}
              {clinicorpWebhook?.last_received_at && (
                <p className={`mt-2 text-xs ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                  Último payload recebido em {new Date(clinicorpWebhook.last_received_at).toLocaleString('pt-BR')}.
                </p>
              )}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {clinicorpWebhookUrl && (
                <button
                  type="button"
                  onClick={copyClinicorpWebhook}
                  disabled={isWebhookBusy}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-sminy-primary px-3 text-sm font-medium text-white transition-colors hover:bg-sminy-primary/90 disabled:opacity-50"
                >
                  <Copy className="h-4 w-4" /> Copiar link
                </button>
              )}
              <button
                type="button"
                onClick={refreshClinicorpWebhook}
                disabled={isWebhookBusy}
                className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors disabled:opacity-50 ${
                  isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-600' : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <RefreshCw className={`h-4 w-4 ${isWebhookBusy ? 'animate-spin' : ''}`} /> Atualizar status
              </button>
              <button
                type="button"
                onClick={() => setConfirmRotateOpen(true)}
                disabled={isWebhookBusy}
                className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors disabled:opacity-50 ${
                  isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-600' : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <Link2 className="h-4 w-4" /> Gerar novo link
              </button>
              {clinicorpWebhook && clinicorpWebhook.status !== 'revoked' && (
                <button
                  type="button"
                  onClick={() => setConfirmRevokeOpen(true)}
                  disabled={isWebhookBusy}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-red-200 px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                >
                  <ShieldOff className="h-4 w-4" /> Revogar
                </button>
              )}
            </div>

            <p className={`mt-4 text-xs ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
              Eventos da agenda de avaliação são processados automaticamente. Identidades, status ou conflitos ambíguos ficam em revisão sem alterar o CRM.
            </p>
          </div>
        )}

        <div className={`border rounded-2xl p-4 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center ${
          isDark 
            ? 'bg-sminy-primary/10 border-sminy-primary/20'
            : 'bg-sminy-primary/5 border-sminy-primary/20'
        }`}>
          <p className={`text-sm flex items-center ${
            isDark ? 'text-sminy-primary/90' : 'text-sminy-primary'
          }`}>
            <Check className="w-4 h-4 mr-2" />
            {clinicorpData.business_id && clinicorpData.dentist_person_id ? 
              'Agendamentos feitos no CRM serão enviados ao Clinicorp com confirmação de sucesso' :
              'Integração configurada, mas faltam detalhes de clínica/profissional'}
          </p>
          
          {(!clinicorpData.business_id || !clinicorpData.dentist_person_id) && (
            <button
              onClick={handleFetchSelectableDetails}
              disabled={isFetchingDetails || isSaving}
              className="inline-flex items-center justify-center space-x-1 px-3 py-2 text-xs rounded-xl bg-sminy-primary text-white hover:bg-sminy-primary/90 disabled:opacity-50 transition-colors"
            >
              <List className="w-3 h-3" />
              <span>{isFetchingDetails ? 'Buscando...' : 'Selecionar Detalhes'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Botões de Ação (para modo de edição)
  const ActionButtons = () => (
    <div className="flex justify-end space-x-3 mt-6">
      <button
        onClick={() => {
          setIsEditing(false);
          if (!hasIntegration) {
            setSelectedIntegration(null);
          } else if (clinicId) {
            fetchIntegrations();
          }
        }}
        disabled={isSaving}
        className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center space-x-2"
      >
        <X className="w-4 h-4" />
        <span>{hasIntegration ? 'Cancelar' : 'Voltar'}</span>
      </button>
      
      <button
        onClick={handleSaveCredentials}
        disabled={
          isSaving || 
          (selectedIntegration === 'clinicorp' && (!clinicorpData.subscriber_id || !clinicorpData.password)) || 
          (selectedIntegration === 'google' && !googleCalendarId)
        }
        className="px-5 py-2 rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Salvando...</span>
          </>
        ) : (
          <>
            <Check className="w-4 h-4" />
            <span>Salvar</span>
          </>
        )}
      </button>
    </div>
  );

  // --- Renderização Principal ---
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className={`min-h-screen p-4 sm:p-8 ${
      isDark ? 'bg-gradient-to-b from-slate-950 to-slate-900' : 'bg-gradient-to-b from-slate-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        {/* 1. Tela inicial - seleção de integração */}
        {!hasIntegration && !selectedIntegration && <IntegrationSelector />}
        
        {/* 2. Modo de edição para integração escolhida */}
        {isEditing && !showSelectionStep && (
          <div className="space-y-6">
            <div className={`pb-6 border-b flex items-center ${
              isDark ? 'border-gray-700' : 'border-slate-200'
            }`}>
              {hasIntegration && (
                <button 
                  onClick={() => { setIsEditing(false); }}
                  className={`mr-4 p-2 rounded-xl transition-colors ${
                    isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              <h1 className={`text-2xl font-bold ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}>
                {hasIntegration ? 'Editar' : 'Configurar'} {selectedIntegration === 'google' ? 'Google Agenda' : 'Clinicorp'}
              </h1>
            </div>
            
            {selectedIntegration === 'google' && <GoogleSetupForm />}
            {selectedIntegration === 'clinicorp' && <ClinicorpSetupForm />}
            
            <ActionButtons />
          </div>
        )}
        
        {/* 3. Modo de seleção de detalhes Clinicorp */}
        {selectedIntegration === 'clinicorp' && showSelectionStep && <ClinicorpSelectionScreen />}
        
        {/* 4. Modo de visualização de integração existente */}
        {hasIntegration && !isEditing && !showSelectionStep && (
          <div className="space-y-6">
            <div className={`pb-6 border-b ${
              isDark ? 'border-gray-700' : 'border-slate-200'
            }`}>
              <h1 className={`text-3xl font-bold ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}>Integração</h1>
              <p className={`mt-2 ${
                isDark ? 'text-gray-300' : 'text-slate-600'
              }`}>
                Gerencie sua conexão com sistemas de agenda
              </p>
            </div>
            
            {selectedIntegration === 'google' && <GoogleConnectedView />}
            {selectedIntegration === 'clinicorp' && <ClinicorpConnectedView />}
          </div>
        )}
      </div>
      <ConfirmDeleteModal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={performDelete}
        title="Remover integração"
        message={`Tem certeza que deseja remover a integração com ${selectedIntegration === 'google' ? 'Google Agenda' : 'Clinicorp'}?`}
        confirmText="Remover"
      />
      <ConfirmDeleteModal
        isOpen={confirmRotateOpen}
        onClose={() => setConfirmRotateOpen(false)}
        onConfirm={regenerateClinicorpWebhook}
        title="Gerar novo link"
        message="O link atual deixará de funcionar imediatamente. Depois, atualize o endereço configurado no Clinicorp."
        confirmText="Gerar novo link"
      />
      <ConfirmDeleteModal
        isOpen={confirmRevokeOpen}
        onClose={() => setConfirmRevokeOpen(false)}
        onConfirm={disableClinicorpWebhook}
        title="Revogar webhook"
        message="O Clinicorp não conseguirá mais enviar eventos para este link até que um novo endereço seja gerado."
        confirmText="Revogar"
      />
    </div>
  );
};

export default AgendaIntegration;
